// Tier 1 §11 risk policy constants

export type RiskProfile = 'Defensive' | 'Cautious' | 'Balanced' | 'Growth' | 'Aggressive';

export const PROFILE_BY_LEVEL: Record<number, RiskProfile> = {
  1: 'Defensive',
  2: 'Cautious',
  3: 'Balanced',
  4: 'Growth',
  5: 'Aggressive',
};

// §11.2 — VaR (95%, 1yr) ceilings, expressed as positive % loss.
// Defaults; live values are read from policy_risk_ceilings.
export const VAR_CEILING: Record<RiskProfile, number> = {
  Defensive: 8,
  Cautious: 10,
  Balanced: 12,
  Growth: 16,
  Aggressive: 20,
};

// §11.3 — Amber 95–100% of ceiling, Red >100%
export type RiskStatus = 'within' | 'amber' | 'breach';
export function riskStatus(value: number | null | undefined, ceiling: number): RiskStatus {
  if (value == null) return 'within';
  const ratio = value / ceiling;
  if (ratio > 1) return 'breach';
  if (ratio >= 0.95) return 'amber';
  return 'within';
}

// Status against a *minimum* (e.g. liquidity coverage must be ≥ ceiling).
export function minRiskStatus(value: number | null | undefined, floor: number): RiskStatus {
  if (value == null) return 'within';
  const ratio = value / floor;
  if (ratio < 1) return 'breach';
  if (ratio < 1.05) return 'amber';
  return 'within';
}

// Annualised tracking error from arrays of daily portfolio + benchmark log returns.
export function trackingError(portfolioValues: number[], benchmarkValues: number[]): number | null {
  const n = Math.min(portfolioValues.length, benchmarkValues.length);
  if (n < 30) return null;
  const diffs: number[] = [];
  for (let i = 1; i < n; i++) {
    const a1 = portfolioValues[i - 1], a2 = portfolioValues[i];
    const b1 = benchmarkValues[i - 1], b2 = benchmarkValues[i];
    if (a1 > 0 && a2 > 0 && b1 > 0 && b2 > 0) {
      diffs.push(Math.log(a2 / a1) - Math.log(b2 / b1));
    }
  }
  if (diffs.length < 30) return null;
  const mean = diffs.reduce((s, x) => s + x, 0) / diffs.length;
  const variance = diffs.reduce((s, x) => s + (x - mean) ** 2, 0) / (diffs.length - 1);
  // Annualise using calendar days — the underlying CBAM Perf sheet stores one row per calendar day.
  return Math.sqrt(variance) * Math.sqrt(365) * 100;
}

// Maximum peak-to-trough drawdown (positive %) over a daily NAV series.
export function maxDrawdown(values: number[]): number | null {
  if (values.length < 2) return null;
  let peak = values[0];
  let maxDd = 0;
  for (const v of values) {
    if (v > peak) peak = v;
    if (peak > 0) {
      const dd = (peak - v) / peak;
      if (dd > maxDd) maxDd = dd;
    }
  }
  return maxDd * 100;
}
